import React, { useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import { toast } from "react-toastify"
import { useConnectedUserNdfDepensesContext } from "./_PersonnelNdfDepenses"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
import MyAlert from "@common-utils/MyAlert"
import MyTooltip from "@common-utils/MyTooltip"
import clsx from "clsx"
import { IPersonnelNdfDetailModel, IPersonnelNdfDocumentModel } from "@common-models/*"
import MyGoogleDriveFileViewerInModal from "../../../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import { deletePersonnelNdfDepenseRequest } from "../core/_requests"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import { CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM, CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_REPAS_MIDI } from "@common-constants/*"

/**
 * Sous-composant pour gérer l'affichage tronqué de la description
 */
const DescriptionCell = ({ description }: { description: string }) => {
     const [isExpanded, setIsExpanded] = useState(false)
     const charLimit = 150

     if (description.length <= charLimit) {
          return (
               <div className={"block mt-2"}>
                    <span className={"fw-bold"}>Description : </span> {description}
               </div>
          )
     }

     return (
          <div className={"block mt-2"}>
               <span className={"fw-bold"}>Description : </span>
               <span className="text-gray-800">{isExpanded ? description : `${description.substring(0, charLimit)}...`}</span>
               <button type="button" className="btn btn-link p-0 ms-2 fw-bolder text-primary fs-9 uppercase transition-all" onClick={() => setIsExpanded(!isExpanded)}>
                    {isExpanded ? "Voir moins" : "Voir plus"}
               </button>
          </div>
     )
}

const SmallCalendarWidget = ({ joursStr, dateString }: { joursStr?: string[] | number[]; dateString?: string }) => {
     if (!joursStr || joursStr.length === 0 || !dateString) return null

     const [year, month] = dateString.split("-").map(Number)
     const daysInMonth = new Date(year, month, 0).getDate()
     const joursKm = joursStr.map(Number)

     return (
          <div className="mt-4 p-3 bg-light-primary bg-opacity-25 border border-primary border-dashed rounded">
               <div className="d-flex flex-wrap gap-1">
                    {Array.from({ length: daysInMonth }, (_, i) => {
                         const d = i + 1
                         const dt = new Date(year, month - 1, d)
                         const isWE = dt.getDay() === 0 || dt.getDay() === 6
                         const letter = new Intl.DateTimeFormat("fr-FR", { weekday: "narrow" }).format(dt)
                         const isSel = joursKm.includes(d)

                         // La condition "return null" a été retirée ici pour afficher tout le mois.

                         return (
                              <div
                                   key={d}
                                   className={clsx(
                                        "d-flex flex-column align-items-center justify-content-center rounded",
                                        isSel
                                             ? "bg-primary text-white shadow-sm"
                                             : isWE
                                             ? "bg-light border border-gray-300 opacity-50"
                                             : "bg-white border border-gray-300 text-gray-700"
                                   )}
                                   style={{ width: "26px", height: "30px", fontSize: "0.65rem" }}
                                   title={isSel ? "Trajet effectué" : isWE ? "Week-end" : "Jour de semaine"}
                              >
                                   <span className="fw-bolder" style={{ lineHeight: "1" }}>
                                        {letter}
                                   </span>
                                   <span className="fw-boldest" style={{ lineHeight: "1", marginTop: "2px" }}>
                                        {d}
                                   </span>
                              </div>
                         )
                    })}
               </div>
          </div>
     )
}

export const PersonnelNdfDepensesListing = () => {
     const MySwal = withReactContent(Swal)
     const context = useConnectedUserNdfDepensesContext()
     const queryClient = useQueryClient()

     const deleteDetailsMutation = useMutation((id: number) => deletePersonnelNdfDepenseRequest(id))

     const [justifInModal, setJustifInModal] = useState<IPersonnelNdfDocumentModel>()

     function handleDeleteDetails(id: number) {
          MySwal.fire({
               icon: "warning",
               text: `Êtes-vous sûr de vouloir supprimer cette dépense?`,
               showCancelButton: true,
               confirmButtonText: "Oui",
               cancelButtonText: "Annuler",
               showLoaderOnConfirm: true,
               preConfirm: async () => {
                    return deleteDetailsMutation
                         .mutateAsync(id)
                         .then(() => {
                              queryClient.setQueryData([context.REACT_QUERY_KEY_GET_LISTING, context.ndfId], (details: IPersonnelNdfDetailModel[] | undefined) => {
                                   const dataLeft = details!.filter(item => item.id !== id)
                                   context.computeTotalExpenses(dataLeft)
                                   return dataLeft
                              })
                              toast.info("La dépense a bien été supprimée")
                         })
                         .catch(e => {
                              toast.error(e?.response?.data?.detail, { autoClose: false })
                         })
               },
               allowOutsideClick: () => !Swal.isLoading(),
          }).then()
     }

     return (
          <>
               {!context.getNdfDepensesQuery.isFetching && context.getNdfDepensesQuery.isError && (
                    <MyAlert type={"danger"} classNames={"mb-4"}>
                         <>
                              <span className={"me-2"}>{context.getNdfDepensesQuery.error?.response?.data?.detail}</span>
                              <button className={"btn btn-sm btn-danger"} onClick={() => context.getNdfDepensesQuery.refetch()}>
                                   Recharger
                              </button>
                         </>
                    </MyAlert>
               )}

               {context.getNdfDepensesQuery.isFetching && (
                    <div className={"text-center p-4"}>
                         <MySimpleSpinner size={"xl"} />
                    </div>
               )}

               {!context.getNdfDepensesQuery.isFetching &&
                    !context.getNdfDepensesQuery.isError &&
                    context.getNdfDepensesQuery.data &&
                    context.getNdfDepensesQuery.data.length === 0 && (
                         <MyAlert type={"primary"} classNames={"mb-4"}>
                              <div>Aucune dépense</div>
                         </MyAlert>
                    )}

               {!context.getNdfDepensesQuery.isFetching &&
                    !context.getNdfDepensesQuery.isError &&
                    context.getNdfDepensesQuery.data &&
                    context.getNdfDepensesQuery.data.length > 0 && (
                         <>
                              <div className={"row p-3"}>
                                   {context.getNdfDepensesQuery.data.map((item, key) => (
                                        <div className="card shadow-sm col-sm-6 col-lg-4 mb-4 p-0 pe-sm-2" key={key}>
                                             <div className="card-header bg-info border border-info" style={{ minHeight: "2rem" }}>
                                                  <h3 className="card-title text-white align-items-center">
                                                       <span className={"fas fa-file-invoice me-2"}></span>
                                                       {item.isParent ? `Dépenses groupées ( ${item.enfants?.length} )` : item.categorieLabel}
                                                  </h3>
                                             </div>
                                             <div className="card-body border-info border">
                                                  {!item.isParent && item.categorie === CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_REPAS_MIDI && (
                                                       <>
                                                            {!item.invites && (
                                                                 <MyAlert type={"danger"} classNames={"mb-4"}>
                                                                      <span className={"fw-bold"}>Repas seul</span>
                                                                 </MyAlert>
                                                            )}

                                                            {item.invites && (
                                                                 <MyAlert type={"primary"} classNames={"mb-4"}>
                                                                      <span className={"fw-bold"}>Repas en groupe</span>
                                                                 </MyAlert>
                                                            )}
                                                       </>
                                                  )}
                                                  <div className="d-flex justify-content-center">
                                                       {item.personnelNdfDocuments?.map((item_, key_) => (
                                                            <MyTooltip title={"Voir la pièce jointe"} key={key_}>
                                                                 <div
                                                                      className={`mb-4 d-flex justify-content-center fas fa-file-invoice cursor-pointer ${key_ > 0 ? "ms-4" : ""}`}
                                                                      style={{ fontSize: "2rem" }}
                                                                      onClick={() => setJustifInModal(item_)}
                                                                 />
                                                            </MyTooltip>
                                                       ))}
                                                  </div>

                                                  {/* Justificatif simple */}
                                                  {!item.isParent && (
                                                       <div className={"mb-4"}>
                                                            <div className={"block"}>
                                                                 <span className={"fw-bold"}>
                                                                      Date :{" "}
                                                                      {item.categorie === CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM
                                                                           ? item.date?.parseZone().format("MMMM YYYY")
                                                                           : item.date?.parseZone().format("DD/MM/YYYY")}
                                                                 </span>
                                                            </div>
                                                            {item.categorie === CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_REPAS_MIDI && item.invites && (
                                                                 <div className={"block"}>
                                                                      <span className={"fw-bold"}>Invités : </span> {item.invites.map(item => item).join(", ")}
                                                                 </div>
                                                            )}
                                                            {item.categorie === CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM && item.indemniteKmDetails && (
                                                                 <>
                                                                      <div className={"block"}>
                                                                           <span className={"fw-bold"}>Départ : </span>{" "}
                                                                           <a
                                                                                href={`https://www.google.com/maps/search/?api=1&query=${item.indemniteKmDetails.addresseDepart}`}
                                                                                target={"_blank"}
                                                                                rel={"noopener noreferrer"}
                                                                           >
                                                                                {item.indemniteKmDetails.addresseDepart}
                                                                           </a>
                                                                      </div>
                                                                      <div className={"block"}>
                                                                           <span className={"fw-bold"}>Destination : </span>{" "}
                                                                           <a
                                                                                href={`https://www.google.com/maps/search/?api=1&query=${item.indemniteKmDetails.addresseArrive}`}
                                                                                target={"_blank"}
                                                                           >
                                                                                {item.indemniteKmDetails.addresseArrive}
                                                                           </a>
                                                                      </div>
                                                                      <div className={"block"}>
                                                                           <span className={"fw-bold"}>Nb trajets : </span>{" "}
                                                                           {item.indemniteKmDetails.isAllerRetour
                                                                                ? item.indemniteKmDetails.nbTrajets! * 2
                                                                                : item.indemniteKmDetails.nbTrajets}
                                                                      </div>
                                                                      <div className={"block"}>
                                                                           <span className={"fw-bold"}>Distance totale : </span>{" "}
                                                                           {item.indemniteKmDetails.distanceKmTotal?.toFixed(2)} Km
                                                                      </div>

                                                                      {/* Intégration du calendrier pour dépense simple */}
                                                                      <SmallCalendarWidget joursStr={item.indemniteKmDetails.joursKm} dateString={item.date?.format("YYYY-MM")} />
                                                                 </>
                                                            )}

                                                            {item.description && <DescriptionCell description={item.description} />}
                                                       </div>
                                                  )}

                                                  {/* Justificatif groupée */}
                                                  {item.isParent && (
                                                       <>
                                                            {item.enfants && item.enfants.length > 0 && (
                                                                 <>
                                                                      {item.enfants.map((child, key) => (
                                                                           <div className={"mb-4"} key={key}>
                                                                                <div className={"block fs-5 border-bottom border-dark mb-1 fw-bold align-items-center"}>
                                                                                     {child.categorie === CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM ? (
                                                                                          "Ticket IK"
                                                                                     ) : (
                                                                                          <>Ticket #{child.numeroTicket}</>
                                                                                     )}
                                                                                </div>
                                                                                {child.categorie === CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_REPAS_MIDI && (
                                                                                     <div className={"my-2"}>
                                                                                          {!child.invites && (
                                                                                               <MyAlert type={"danger"}>
                                                                                                    <span className={"fw-bold"}>Repas seul</span>
                                                                                               </MyAlert>
                                                                                          )}

                                                                                          {child.invites && (
                                                                                               <MyAlert type={"primary"}>
                                                                                                    <span className={"fw-bold"}>Repas en groupe</span>
                                                                                               </MyAlert>
                                                                                          )}
                                                                                     </div>
                                                                                )}
                                                                                <div className={"block"}>
                                                                                     <span className={"fw-bold"}>Date : </span>{" "}
                                                                                     {child.categorie === CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM
                                                                                          ? child.date?.parseZone().format("MMMM YYYY")
                                                                                          : child.date?.parseZone().format("DD/MM/YYYY")}
                                                                                </div>
                                                                                <div className={"block"}>
                                                                                     <span className={"fw-bold"}>Catégorie : </span> {child.categorieLabel}
                                                                                </div>
                                                                                <div className={"block"}>
                                                                                     <span className={"fw-bold"}>Montant TTC : </span> {child.montantTtc?.toFixed(2)} €
                                                                                </div>
                                                                                {child.categorie === CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_REPAS_MIDI && child.invites && (
                                                                                     <div className={"block"}>
                                                                                          <span className={"fw-bold"}>Invités : </span>{" "}
                                                                                          {child.invites?.map(item => item).join(", ")}
                                                                                     </div>
                                                                                )}
                                                                                {child.categorie === CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM && child.indemniteKmDetails && (
                                                                                     <>
                                                                                          <div className={"block"}>
                                                                                               <span className={"fw-bold"}>Départ : </span>{" "}
                                                                                               <a
                                                                                                    href={`https://www.google.com/maps/search/?api=1&query=${child.indemniteKmDetails.addresseDepart}`}
                                                                                                    target={"_blank"}
                                                                                                    rel={"noopener noreferrer"}
                                                                                               >
                                                                                                    {child.indemniteKmDetails.addresseDepart}
                                                                                               </a>
                                                                                          </div>
                                                                                          <div className={"block"}>
                                                                                               <span className={"fw-bold"}>Destination : </span>{" "}
                                                                                               <a
                                                                                                    href={`https://www.google.com/maps/search/?api=1&query=${child.indemniteKmDetails.addresseArrive}`}
                                                                                                    target={"_blank"}
                                                                                               >
                                                                                                    {child.indemniteKmDetails.addresseArrive}
                                                                                               </a>
                                                                                          </div>
                                                                                          <div className={"block"}>
                                                                                               <span className={"fw-bold"}>Nb trajets : </span>{" "}
                                                                                               {child.indemniteKmDetails.isAllerRetour
                                                                                                    ? child.indemniteKmDetails.nbTrajets! * 2
                                                                                                    : child.indemniteKmDetails.nbTrajets}
                                                                                          </div>

                                                                                          <div className={"block"}>
                                                                                               <span className={"fw-bold"}>Distance totale : </span>{" "}
                                                                                               {child.indemniteKmDetails.distanceKmTotal?.toFixed(2)} Km
                                                                                          </div>

                                                                                          {/* Intégration du calendrier pour dépense enfant (groupée) */}
                                                                                          <SmallCalendarWidget
                                                                                               joursStr={child.indemniteKmDetails.joursKm}
                                                                                               dateString={child.date?.format("YYYY-MM")}
                                                                                          />
                                                                                     </>
                                                                                )}

                                                                                {child.description && <DescriptionCell description={child.description} />}
                                                                           </div>
                                                                      ))}
                                                                 </>
                                                            )}
                                                       </>
                                                  )}

                                                  <div className="row border-top-dashed border-info pt-3">
                                                       <div className="col-6 p-0">
                                                            {context.editionMode && (
                                                                 <>
                                                                      <button
                                                                           type="button"
                                                                           className="btn btn-sm btn-warning p-0 px-3 py-1 fs-4 me-3"
                                                                           onClick={() => context.setCurrentDepenseInEdition(item)}
                                                                      >
                                                                           <span className={"fas fa-edit"} />
                                                                      </button>
                                                                      <button
                                                                           type="button"
                                                                           className="btn btn-sm btn-danger p-0 px-3 py-1 fs-4"
                                                                           onClick={() => handleDeleteDetails(item.id as number)}
                                                                      >
                                                                           <span className={"fas fa-trash"} />
                                                                      </button>
                                                                 </>
                                                            )}
                                                       </div>
                                                       <div className="col-6 fw-bold fs-6 d-flex align-items-center p-0 justify-content-end text-nowrap">
                                                            Total :{" "}
                                                            {item.isParent
                                                                 ? item.enfants
                                                                      ? Object.values(item.enfants.map(child => child.montantTtc as number))
                                                                             .reduce((acc, val) => acc + val, 0)
                                                                             .toFixed(2)
                                                                      : 0
                                                                 : item.montantTtc?.toFixed(2)}{" "}
                                                            €
                                                       </div>
                                                  </div>
                                             </div>
                                        </div>
                                   ))}
                              </div>
                              {context.getNdfDepensesQuery.data.length > 1 && (
                                   <div className="d-flex justify-content-end me-2">
                                        <div className={"alert alert-info px-2 py-1 text-dark fw-bold m-0"}>TOTAL: {context.totalDepenses.toFixed(2)} €</div>
                                   </div>
                              )}
                         </>
                    )}

               {/* Modal view justificatif*/}
               {justifInModal && (
                    <MyGoogleDriveFileViewerInModal
                         googleDriveID={justifInModal.googleDriveID as string}
                         googleDriveMimetype={justifInModal.googleDriveMimeType as string}
                         modalTitle={<span>Justificatif</span>}
                         handleCloseModal={() => setJustifInModal(undefined)}
                         modalSize={"lg"}
                    />
               )}
          </>
     )
}
